import type { ReactNode } from 'react';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/** Tones carry emphasis, never meaning on their own: every badge also states its status in words. */
export type BadgeTone = 'neutral' | 'good' | 'warn' | 'danger';

const VARIANT: Record<BadgeTone, NonNullable<BadgeProps['variant']>> = {
  neutral: 'neutral',
  good: 'success',
  warn: 'warning',
  danger: 'danger',
};

/**
 * A status as a pill: a dot in the tone's own colour, then the word. The dot is drawn by `before:`, so it adds
 * nothing to the text a test or a screen reader reads. `badge badge-${tone}` stays on the element as a hook:
 * the tests read the tone from it.
 */
export function StatusBadge({ tone, children }: { tone: BadgeTone; children: ReactNode }) {
  return (
    <Badge
      variant={VARIANT[tone]}
      className={cn(
        `badge badge-${tone}`,
        'before:size-1.5 before:shrink-0 before:rounded-full before:bg-current',
      )}
    >
      {children}
    </Badge>
  );
}
